/**
 * SidebarPostPopover Component
 *
 * Centralized popover content for posts in the sidebar (draggable, unscheduled posts).
 * Used by: DraggableGridPostGroup (for both posts and variations)
 */

import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faYoutube,
  faTwitter,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import { faCloud } from '@fortawesome/free-solid-svg-icons';
import { Post, PostVariationSummary, OccurrenceStatus } from '@/services/posts';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { getPlatformDisplayName } from '@/constants/platformLimits';
import { useReleaseCapabilities } from '@/hooks/useReleaseCapabilities';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { CarouselThumb } from './CarouselThumb';
import { CaptionWithReadMore } from './CaptionWithReadMore';

// Platform icons mapping
const platformIcons: Record<string, IconDefinition> = {
  FACEBOOK: faFacebook,
  INSTAGRAM: faInstagram,
  LINKEDIN: faLinkedin,
  TWITTER: faTwitter,
  YOUTUBE: faYoutube,
  GOOGLE_MY_BUSINESS: faGoogle,
  PINTEREST: faPinterest,
  TIKTOK: faTiktok,
  THREADS: faThreads,
  BLUESKY: faCloud,
};

// Platform colors for icons
const platformColors: Record<string, string> = {
  FACEBOOK: 'text-[#1877F2]',
  INSTAGRAM: 'text-[#E4405F]',
  LINKEDIN: 'text-[#0A66C2]',
  TWITTER: 'text-[#1DA1F2]',
  YOUTUBE: 'text-[#FF0000]',
  GOOGLE_MY_BUSINESS: 'text-[#4285F4]',
  PINTEREST: 'text-[#BD081C]',
  TIKTOK: 'text-[#000000]',
  THREADS: 'text-[#000000]',
  BLUESKY: 'text-[#0085FF]',
};

/**
 * Legacy fallback: maps the 4-icon fold `getInferredStatusBadge` produces
 * when `post.displayStatus`/`variation.displayStatus` is absent (Failed >
 * Published > Scheduled > Mapped, see DraggableGridPostGroup.tsx) back to the
 * matching first-class `OccurrenceStatus`, so `getFooterHint` below has a
 * single ENUM to switch on either way.
 */
const LEGACY_ICON_TO_STATUS: Partial<Record<string, OccurrenceStatus>> = {
  [ICONS.WARNING]: 'FAILED',
  [ICONS.CHECK]: 'PUBLISHED',
  [ICONS.CLOCK]: 'SCHEDULED',
  [ICONS.CALENDAR]: 'MAPPED',
};

/**
 * Decorative emoji for the header's `inferredStatus` badge (Task #182 R4-5
 * #1). Purely cosmetic — the badge label/color already carry the real
 * meaning (see `getInferredStatusBadge` in `utils/calendarStatus.ts`); this
 * just gives each `ICONS.*` value used there a distinct glyph instead of
 * every non-CHECK/WARNING/CLOCK status collapsing to the generic 📌.
 */
const STATUS_ICON_TO_EMOJI: Partial<Record<string, string>> = {
  [ICONS.CHECK]: '✅',
  [ICONS.WARNING]: '❌',
  [ICONS.CLOCK]: '🕐',
  [ICONS.PARTIAL]: '◐',
  [ICONS.MISSED]: '⏰',
  [ICONS.CROSS]: '🚫',
  [ICONS.LOADER]: '🔄',
};

const getInferredStatusEmoji = (icon: string): string => STATUS_ICON_TO_EMOJI[icon] ?? '📌';

/**
 * Get contextual footer hint based on content + occurrence status.
 *
 * Branches on the underlying ENUM values — `contentStatus` (DRAFT/READY, raw
 * from `post`/`variation`) and the resolved `OccurrenceStatus` (preferably
 * `post.displayStatus`/`variation.displayStatus`, the server-computed
 * latest-non-cancelled occurrence per POST-STATUS-MODEL.md; falling back to
 * decoding `inferredStatusIcon` when `displayStatus` isn't populated yet).
 * NEVER matches translated display labels (e.g. `label === 'Draft'`), since
 * `contentStatus.label`/`inferredStatus.label` are already `t()`-translated
 * by the time they reach this component, and that silently breaks for every
 * non-English locale.
 *
 * The inner switch is exhaustive over all 8 first-class `OccurrenceStatus`
 * values (the `never` default makes a 9th status a COMPILE error, not a
 * silent fallthrough to the DRAFT/READY hint — the C4 regression this fixes).
 */
function getFooterHint(
  contentStatus: 'DRAFT' | 'READY' | undefined,
  displayStatus: OccurrenceStatus | undefined,
  inferredStatusIcon: string | undefined,
  workflowActive: boolean,
  canReleaseDirect: boolean,
  releasePermissionLoading: boolean,
  t: (key: string, options?: { defaultValue: string }) => string
): string {
  // Content is draft: name the next step in this brand's publishing flow.
  if (contentStatus === 'DRAFT') {
    if (!workflowActive) return t('popover_hint_draft', { defaultValue: 'Draft: mark it Ready to schedule' });
    if (releasePermissionLoading) return t('popover_hint_draft_open', { defaultValue: 'Draft: open to continue' });
    return canReleaseDirect
      ? t('popover_hint_draft_release', { defaultValue: 'Draft: release it to schedule' })
      : t('popover_hint_draft_review', { defaultValue: 'Draft: send for review to schedule' });
  }

  const resolvedStatus: OccurrenceStatus | undefined =
    displayStatus ?? (inferredStatusIcon ? LEGACY_ICON_TO_STATUS[inferredStatusIcon] : undefined);

  // Occurrence status takes priority when content is ready or unknown
  if (resolvedStatus) {
    switch (resolvedStatus) {
      case 'MAPPED':
        return t('popover_hint_mapped', { defaultValue: 'Mapped — ready to schedule' });
      case 'SCHEDULED':
        return t('popover_hint_scheduled', { defaultValue: 'Scheduled — drag to add more dates' });
      case 'PUBLISHING':
        return t('popover_hint_publishing', { defaultValue: 'Publishing now — hang tight' });
      case 'PUBLISHED':
        return t('popover_hint_published', { defaultValue: 'Drag to schedule again' });
      case 'PARTIAL':
        return t('popover_hint_partial', { defaultValue: 'Some channels failed — review and retry' });
      case 'FAILED':
        return t('popover_hint_failed', { defaultValue: 'Fix errors and reschedule' });
      case 'MISSED':
        return t('popover_hint_missed', { defaultValue: 'Missed — drag to reschedule' });
      case 'CANCELLED':
        return t('popover_hint_cancelled', { defaultValue: 'Cancelled — drag to schedule again' });
      default: {
        const _exhaustive: never = resolvedStatus;
        return _exhaustive;
      }
    }
  }

  // Content is ready, not yet on calendar
  if (contentStatus === 'READY') {
    return t('popover_hint_ready', { defaultValue: 'Drag to map or schedule' });
  }

  // Fallback: unmapped, no specific status
  return t('popover_hint_unmapped', { defaultValue: 'Drag to calendar to schedule' });
}

interface SidebarPostPopoverProps {
  post: Post;
  variation?: PostVariationSummary;
  contentStatus?: { color: string; bgColor: string; label: string; emoji: string } | null;
  inferredStatus?: { color: string; bgColor: string; label: string; icon: string; count?: number } | null;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const SidebarPostPopover = ({
  post,
  variation,
  contentStatus,
  inferredStatus,
  onEdit,
  onDelete,
}: SidebarPostPopoverProps) => {
  const { t } = useTranslation('common');

  // If variation is provided, use its values where available
  const effectiveMedia = variation?.postMedia?.length ? variation.postMedia : post.postMedia;
  const effectiveCaption = variation?.caption || post.caption || '';
  // Raw content-status ENUM (DRAFT/READY) mirroring how the caller derived
  // the `contentStatus` badge prop — variation's own status when a variation
  // is shown, else the parent post's. Used for enum-based footer-hint
  // branching (never the translated `contentStatus.label`).
  const effectiveContentStatus = variation ? variation.contentStatus : post.contentStatus;
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  const workflowActive = isAgencyTeam && !carteBlanche;
  const { canReleaseDirect, isLoading: releasePermissionLoading } = useReleaseCapabilities(effectiveContentStatus === 'DRAFT');
  // Raw occurrence-status ENUM (OccurrenceStatus), mirroring the same
  // variation-vs-post selection as `effectiveContentStatus` above — used for
  // enum-based footer-hint branching (never the translated `inferredStatus.label`).
  const effectiveDisplayStatus = (variation ? variation.displayStatus : post.displayStatus) ?? undefined;
  const firstMedia = effectiveMedia?.[0];
  const mediaCount = effectiveMedia?.length ?? 0;
  const variationsCount = post.variations?.length ?? 0;
  const hasVariations = !variation && variationsCount > 0; // Only show for posts, not variations
  const footerHint = getFooterHint(
    effectiveContentStatus,
    effectiveDisplayStatus,
    inferredStatus?.icon,
    workflowActive,
    canReleaseDirect,
    releasePermissionLoading,
    t
  );

  return (
    <div className="flex max-h-[calc(85dvh-18px)] min-h-0 flex-col lg:h-auto lg:max-h-[70vh]">
      {/* Header — fixed */}
      <div className={`bg-gradient-to-r ${variation ? 'from-secondary-500 to-secondary-600' : 'from-primary-500 to-primary-600'} px-4 pr-14 lg:pr-4 py-3 flex-shrink-0`}>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Variation title badge (for variations) */}
          {variation && (
            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-white text-gray-700 flex items-center gap-1">
              <Icon name={ICONS.COPY} size="xs" ariaHidden />
              {variation.title}
            </span>
          )}
          {/* Inferred status badge (primary) */}
          {inferredStatus ? (
            <span className="px-1.5 py-0.5 text-xs font-semibold rounded flex items-center gap-1 bg-white text-gray-700">
              <span>{getInferredStatusEmoji(inferredStatus.icon)}</span>
              {inferredStatus.label}
              {inferredStatus.count !== undefined && inferredStatus.count > 1 && (
                <span className="opacity-75">({inferredStatus.count})</span>
              )}
            </span>
          ) : (
            <span className="px-1.5 py-0.5 text-xs font-semibold rounded flex items-center gap-1 bg-white text-gray-700">
              <span>📭</span> {t('status_unmapped', { defaultValue: 'Not on calendar' })}
            </span>
          )}
          {/* Content status badge (secondary) */}
          {contentStatus && (
            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-white text-gray-700">
              {contentStatus.emoji} {contentStatus.label}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto min-h-0">
      {/* Thumbnail - full width square with carousel rotation */}
      <div className="p-[9px]">
        <CarouselThumb media={effectiveMedia || []} mediaCount={mediaCount} firstMedia={firstMedia} />
      </div>

      {/* Content */}
      <div className="p-4">
        <CaptionWithReadMore caption={effectiveCaption} />

        {/* Platforms & Stats */}
        {post.postChannels && post.postChannels.length > 0 && (
          <div className="flex items-center justify-between gap-3">
            {/* Platform icons */}
            <div className="flex items-center gap-2">
              {/* Get unique platforms */}
              {[...new Set(post.postChannels.map(pc => pc.channel.platform))].slice(0, 5).map((platform) => (
                <Tooltip key={platform} content={getPlatformDisplayName(platform)}>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={platformIcons[platform] || faCloud}
                      className={cn('w-4 h-4', platformColors[platform] || 'text-gray-400')}
                      aria-hidden="true"
                    />
                  </div>
                </Tooltip>
              ))}
            </div>

            {/* Aggregate stats */}
            {post.instancesSummary && (post.instancesSummary.scheduled > 0 || post.instancesSummary.published > 0) && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                {post.instancesSummary.scheduled > 0 && (
                  <span className="flex items-center gap-1">
                    <Icon name={ICONS.CLOCK} size="xs" className="text-blue-500" ariaHidden />
                    {post.instancesSummary.scheduled}
                  </span>
                )}
                {post.instancesSummary.published > 0 && (
                  <span className="flex items-center gap-1">
                    <Icon name={ICONS.CHECK} size="xs" className="text-emerald-500" ariaHidden />
                    {post.instancesSummary.published}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Variations count (only for posts, not variations) */}
        {hasVariations && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Icon name={ICONS.COPY} size="xs" className="text-secondary-500" ariaHidden />
              {variationsCount} {t('variations', { defaultValue: 'variations' })}
            </span>
          </div>
        )}
      </div>

      </div>{/* End scrollable content */}

      {/* Footer — fixed */}
      <div className={cn('flex-shrink-0 px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-center lg:justify-between gap-2', effectiveContentStatus === 'DRAFT' ? 'flex-nowrap' : 'flex-wrap')}>
        {effectiveContentStatus === 'DRAFT' && onEdit ? (
          <button
            type="button"
            data-close-sheet
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
            className="inline-flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-lg border border-transparent px-2 text-left text-xs font-medium text-primary-700 transition-colors hover:bg-primary-50 focus-visible:border-primary-500 focus-visible:bg-primary-50 focus-visible:outline-none"
          >
            <Icon name={ICONS.EDIT} size="xs" ariaHidden />
            <span>{footerHint}</span>
          </button>
        ) : (
          <span className={cn('text-xs text-gray-500', effectiveContentStatus !== 'DRAFT' && 'hidden lg:inline')}>
            {footerHint}
          </span>
        )}
        {(onEdit || onDelete) && (
          <div className="flex flex-shrink-0 items-center gap-1">
            {onEdit && effectiveContentStatus !== 'DRAFT' && (
              <button
                data-close-sheet
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="flex min-h-[44px] items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                aria-label={t('edit')}
              >
                <Icon name={ICONS.EDIT} size="xs" ariaHidden />
                {t('edit')}
              </button>
            )}
            {onDelete && (
              <button
                data-close-sheet
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="flex min-h-[44px] items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                aria-label={t('delete')}
              >
                <Icon name={ICONS.TRASH} size="xs" ariaHidden />
                {t('delete')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
